import React, { useEffect, useMemo, useRef, useState } from "react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import ProductCard from "@/components/ProductCard";
import Footer from "@/components/Footer";
import AnnouncementBar from "@/components/AnnouncementBar";
import HeroCarousel from "@/components/HeroCarousel";
import HandmadeShowcase from "@/components/HandmadeShowcase";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, MapPin, Clock, Phone, Instagram, Gift } from "lucide-react";

// PERFORMANCE: 20.000 ürünü hepsini client'a yüklemek sayfayı donduruyordu.
// Çözüm: tamamen sunucu-taraflı sayfalama + arama + kategori filtresi.
// İlk açılışta sadece 60 ürün gelir; "Daha fazla göster" ile 60'ar artar.
// Arama debounced olarak sunucuya gider.
const PAGE_SIZE = 60;

export default function Home() {
  const { lang, t, settings } = useApp();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [activeCat, setActiveCat] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalProducts, setTotalProducts] = useState(0);
  const reqSeq = useRef(0); // ignore stale responses

  // Debounce search → 300ms
  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  // Load categories + featured once
  useEffect(() => {
    (async () => {
      try {
        const [cRes, fRes] = await Promise.all([
          api.get("/categories"),
          api.get("/products?limit=8", { params: {} }),
        ]);
        setCategories(cRes.data || []);
        // "Öne çıkan" olarak gerçekten featured=true olanları öncele, yoksa ilk 4
        const items = fRes.data || [];
        const feat = items.filter((p) => p.featured).slice(0, 4);
        setFeatured(feat.length ? feat : items.slice(0, 4));
      } catch {}
    })();
  }, []);

  // Load first page whenever filter/search changes
  useEffect(() => {
    const seq = ++reqSeq.current;
    setLoading(true);
    (async () => {
      try {
        const params = { skip: 0, limit: PAGE_SIZE };
        if (activeCat !== "all") params.category = activeCat;
        if (debouncedSearch) params.search = debouncedSearch;
        const res = await api.get("/products", { params });
        if (seq !== reqSeq.current) return;
        const total = parseInt(res.headers?.["x-total-count"] || "0", 10) || res.data.length;
        setProducts(res.data || []);
        setTotalProducts(total);
      } finally {
        if (seq === reqSeq.current) setLoading(false);
      }
    })();
  }, [activeCat, debouncedSearch]);

  const loadMore = async () => {
    if (loadingMore || products.length >= totalProducts) return;
    setLoadingMore(true);
    try {
      const params = { skip: products.length, limit: PAGE_SIZE };
      if (activeCat !== "all") params.category = activeCat;
      if (debouncedSearch) params.search = debouncedSearch;
      const res = await api.get("/products", { params });
      setProducts((prev) => [...prev, ...(res.data || [])]);
    } finally {
      setLoadingMore(false);
    }
  };

  const sortedCats = useMemo(
    () => [...categories].sort((a, b) => (a.name_tr || "").localeCompare(b.name_tr || "", "tr")),
    [categories]
  );

  const scrollToProducts = () => {
    document.getElementById("catalog")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Header onSearch={setSearch} searchValue={search} />
      <AnnouncementBar />

      {/* CATEGORY STRIP — sticky */}
      <section
        data-testid="category-strip"
        className="bg-[#FAF7F2] border-b border-[#E8E2D5] sticky top-0 z-30 backdrop-blur bg-opacity-95"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center gap-2 overflow-x-auto">
          <button
            data-testid="cat-chip-all"
            onClick={() => { setActiveCat("all"); scrollToProducts(); }}
            className={`shrink-0 h-9 px-4 rounded-full font-heading font-bold text-sm border transition ${
              activeCat === "all" ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
            }`}
          >
            Hepsi
          </button>
          {sortedCats.map((cat) => (
            <button
              key={cat.slug}
              data-testid={`cat-chip-${cat.slug}`}
              onClick={() => { setActiveCat(cat.slug); scrollToProducts(); }}
              className={`shrink-0 h-9 px-4 rounded-full font-heading font-bold text-sm border transition whitespace-nowrap ${
                activeCat === cat.slug ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
              }`}
            >
              <span className="mr-1">{cat.icon || "🛒"}</span>{cat.name_tr}
            </button>
          ))}
        </div>
      </section>

      {/* HERO — Sponsor carousel if banners exist (schedule aware), else static welcome hero */}
      {Array.isArray(settings?.sponsor_banners) && settings.sponsor_banners.some((b) => {
        if (!b || b.enabled === false || !b.image_url) return false;
        const now = Date.now();
        const s = b.start_at ? new Date(b.start_at).getTime() : null;
        const e = b.end_at ? new Date(b.end_at).getTime() : null;
        if (s && now < s) return false;
        if (e && now > e) return false;
        return true;
      }) ? (
        <HeroCarousel
          banners={settings.sponsor_banners}
          rotationSeconds={settings?.banner_rotation_seconds || 6}
        />
      ) : (
        <section
          data-testid="hero-section"
          className="relative overflow-hidden border-b border-[#E8E2D5] bg-gradient-to-b from-[#2E5A36] via-[#1B2A22] to-[#1B2A22] text-[#FAF7F2]"
        >
        <div aria-hidden="true" className="pointer-events-none select-none absolute inset-0 flex items-center justify-center overflow-hidden">
          <span className="font-heading font-black uppercase tracking-tighter text-[22vw] leading-none text-[#FAF7F2]/[0.06] whitespace-nowrap">
            İKİZLER&nbsp;MARKET
          </span>
        </div>
        <img
          src="/logo-ikizler.png"
          alt="İkizler Market"
          className="pointer-events-none select-none absolute top-5 right-5 w-16 sm:w-20 h-auto rounded-lg bg-white/95 p-1.5 shadow-xl z-10"
        />
        <div className="absolute -top-16 -left-16 w-72 h-72 bg-[#E5A024]/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -right-10 w-96 h-96 bg-[#D9653B]/20 rounded-full blur-3xl" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 text-center">
          <Badge data-testid="hero-badge" className="bg-[#E5A024]/20 text-[#E5A024] border border-[#E5A024]/40 hover:bg-[#E5A024]/20 rounded-full px-4 py-1.5 font-heading">
            <Sparkles className="w-3.5 h-3.5 mr-1.5" />
            {t("campus_delivery_badge")}
          </Badge>
          <h1 data-testid="hero-headline" className="font-heading font-extrabold leading-[0.95] tracking-tight mt-6 text-5xl sm:text-7xl lg:text-8xl drop-shadow-[0_2px_12px_rgba(0,0,0,0.35)]">
            HOŞ GELDİNİZ
          </h1>
          <p className="text-xl sm:text-2xl font-heading text-[#E5A024] mt-3">İkizler Market Lefke</p>
          <p className="text-sm sm:text-base text-[#FAF7F2]/80 leading-relaxed max-w-2xl mx-auto mt-5">
            {t("tagline_2")}
          </p>
          <div className="flex flex-wrap gap-3 justify-center mt-7">
            <Button data-testid="hero-cta-primary" onClick={scrollToProducts} className="h-12 px-7 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold text-base shadow-lg">
              {t("hero_cta_primary")}
            </Button>
            <a data-testid="hero-cta-whatsapp" href="https://wa.me/905391095756" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="h-12 px-7 bg-white/10 border-white/30 hover:bg-white/20 text-[#FAF7F2] rounded-full font-heading font-bold text-base backdrop-blur">
                📱 {t("hero_cta_secondary")}
              </Button>
            </a>
          </div>

          <div className="grid grid-cols-3 gap-3 sm:gap-4 max-w-2xl mx-auto mt-10">
            {[
              { icon: MapPin, label: "Karadağ / Lefke", sub: "YERİNDE TESLİMAT" },
              { icon: Clock, label: "07:30-23:30", sub: "HER GÜN" },
              { icon: Phone, label: "WhatsApp", sub: "HIZLI SİPARİŞ" },
            ].map((s, i) => (
              <div key={i} className="bg-white/10 backdrop-blur border border-white/15 rounded-xl p-3 text-left">
                <s.icon className="w-4 h-4 text-[#E5A024] mb-2" />
                <p className="text-xs font-heading font-bold text-[#FAF7F2]">{s.label}</p>
                <p className="text-[10px] text-[#FAF7F2]/60 uppercase tracking-wider">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* HANDMADE SHOWCASE — hero'nun hemen altında, iştah açıcı el yapımı kartlar */}
      <HandmadeShowcase />

      {/* FEATURED */}
      {featured.length > 0 && !debouncedSearch && activeCat === "all" && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex items-end justify-between mb-6">
            <div>
              <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A22]">
                {t("featured_title")}
              </h2>
              <p className="text-sm text-[#1B2A22]/60 mt-1">
                {lang === "tr" ? "En sevilen ürünler" : "Most loved picks"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {featured.map((p) => (<ProductCard key={p.id} product={p} />))}
          </div>
        </section>
      )}

      {/* INSTAGRAM DISCOUNT CTA */}
      <section data-testid="home-ig-block" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] p-6 sm:p-8 text-white shadow-xl">
          <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
          <div className="absolute -left-8 -bottom-8 w-56 h-56 bg-[#E5A024]/30 rounded-full blur-3xl" />
          <div className="relative grid md:grid-cols-[1fr_auto] gap-4 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur rounded-full px-3 py-1 mb-3 text-xs font-heading font-bold">
                <Gift className="w-3.5 h-3.5 text-[#E5A024]" />
                {t("ig_home_badge")}
              </div>
              <h2 className="font-heading font-extrabold text-2xl sm:text-3xl mb-2">{t("ig_home_title")}</h2>
              <p className="text-sm sm:text-base opacity-90 max-w-xl">{t("ig_home_desc")}</p>
              <ul className="mt-3 grid gap-1 text-sm">
                <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs">1</span>{t("ig_home_step1")}</li>
                <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs">2</span>{t("ig_home_step2")}</li>
                <li className="flex items-center gap-2"><span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center font-bold text-xs">3</span>{t("ig_home_step3")}</li>
              </ul>
              <p className="text-[11px] opacity-70 mt-3 max-w-xl">{t("ig_home_note")}</p>
            </div>
            <a data-testid="home-ig-link" href="https://www.instagram.com/ikizlermarketlefke" target="_blank" rel="noopener noreferrer" className="shrink-0">
              <Button className="h-14 px-6 bg-white hover:bg-white/90 text-[#1B2A22] font-heading font-extrabold rounded-full text-base shadow-lg">
                <Instagram className="w-5 h-5 mr-2 text-[#DD2A7B]" />
                {t("ig_home_cta")}
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* CATALOG */}
      <section id="catalog" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-6 flex items-end justify-between gap-3 flex-wrap">
          <h2 className="font-heading font-bold text-2xl sm:text-3xl text-[#1B2A22]">
            {t("all_products")}
          </h2>
          {!loading && (
            <p className="text-xs text-[#1B2A22]/60 font-mono">
              {products.length} / {totalProducts}
            </p>
          )}
        </div>

        {/* Category pills */}
        <div data-testid="category-pills" className="flex overflow-x-auto no-scrollbar gap-2 pb-4 mb-6 -mx-4 px-4">
          <button
            data-testid="category-all"
            onClick={() => setActiveCat("all")}
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-heading font-semibold border transition-all min-h-[44px] ${
              activeCat === "all" ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
            }`}
          >
            🛒 {t("all")}
          </button>
          {categories.map((c) => (
            <button
              key={c.id}
              data-testid={`category-${c.slug}`}
              onClick={() => setActiveCat(c.slug)}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-heading font-semibold border transition-all min-h-[44px] whitespace-nowrap ${
                activeCat === c.slug ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]" : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
              }`}
            >
              {c.icon} {lang === "tr" ? c.name_tr : c.name_en}
            </button>
          ))}
        </div>

        {/* Products grid */}
        {loading ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-white/60 rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-2xl border border-[#E8E2D5]">
            <p className="text-[#1B2A22]/60">{t("no_products")}</p>
          </div>
        ) : (
          <>
            <div data-testid="products-grid" className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((p) => (<ProductCard key={p.id} product={p} />))}
            </div>
            {products.length < totalProducts && (
              <div className="flex justify-center mt-8">
                <Button
                  data-testid="load-more-btn"
                  onClick={loadMore}
                  disabled={loadingMore}
                  className="h-12 px-8 bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full font-heading font-bold"
                >
                  {loadingMore ? "Yükleniyor..." : `Daha Fazla Göster (${totalProducts - products.length})`}
                </Button>
              </div>
            )}
          </>
        )}
      </section>

      <Footer />
    </div>
  );
}
